import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { API } from '../../lib/api';
import { toast } from 'sonner';
import {
  Search, User, Phone, Mail, Euro, Home, Star, ChevronRight,
  Crown, TrendingUp, Repeat, Users, Filter, RefreshCw, Gift, Award
} from 'lucide-react';

// Smart Tag (compact) - high contrast colors
const SmartTag = ({ tag, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-700 border border-blue-200',
    green: 'bg-green-100 text-green-700 border border-green-200',
    gold: 'bg-amber-100 text-amber-700 border border-amber-200',
    purple: 'bg-purple-100 text-purple-700 border border-purple-200',
    red: 'bg-red-100 text-red-700 border border-red-200',
    orange: 'bg-orange-100 text-orange-700 border border-orange-200',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.blue}`}>
      {tag}
    </span>
  );
};

// Guest Row - Light theme with high contrast
const GuestRow = ({ guest }) => (
  <Link
    to={`/admin/crm/guests/${encodeURIComponent(guest.id)}/360`}
    className="flex items-center justify-between p-4 hover:bg-gray-50 border-b border-gray-100 transition-colors group"
    data-testid={`guest-row-${guest.id}`}
  >
    <div className="flex items-center gap-4 min-w-0 flex-1">
      <div className="w-12 h-12 bg-gradient-to-br from-[#C5A059] to-[#A8894A] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
        <span className="text-white font-semibold text-lg">
          {guest.name?.charAt(0)?.toUpperCase() || '?'}
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-base font-semibold text-gray-900 truncate">{guest.name || 'N/A'}</p>
        <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
          {guest.email && (
            <span className="flex items-center gap-1 truncate max-w-[200px]">
              <Mail className="w-3.5 h-3.5" />
              {guest.email}
            </span>
          )}
          {guest.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" />
              {guest.phone}
            </span>
          )}
        </div>
      </div>
    </div>
    
    <div className="flex items-center gap-6">
      {/* Tags */}
      <div className="flex gap-1.5 flex-wrap justify-end max-w-[200px] hidden lg:flex">
        {guest.tags?.slice(0, 3).map((t, i) => (
          <SmartTag key={i} tag={t.tag} color={t.color} />
        ))}
      </div>
      
      {/* Stays */}
      <div className="text-center hidden sm:block min-w-[70px]">
        <p className="text-lg font-bold text-gray-900">{guest.total_stays}</p>
        <p className="text-xs text-gray-500 uppercase tracking-wide">Soggiorni</p>
      </div>
      
      {/* Revenue */}
      <div className="text-center hidden md:block min-w-[80px]">
        <p className="text-lg font-bold text-green-600">€{guest.total_spent?.toFixed(0)}</p>
        <p className="text-xs text-gray-500 uppercase tracking-wide">Totale</p>
      </div>

      {/* Loyalty Points */}
      <div className="text-center hidden lg:block min-w-[70px]">
        <p className={`text-lg font-bold ${guest.loyalty_points > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
          {guest.loyalty_points || 0}
        </p>
        <p className="text-xs text-gray-500 uppercase tracking-wide">Punti</p>
      </div>
      
      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#C5A059] transition-colors" />
    </div>
  </Link>
);

// Stats Summary - Clean card design
const StatsSummary = ({ guests }) => {
  const totalGuests = guests.length;
  const totalStays = guests.reduce((sum, g) => sum + (g.total_stays || 0), 0);
  const totalSpent = guests.reduce((sum, g) => sum + (g.total_spent || 0), 0);
  const returningGuests = guests.filter(g => g.total_stays > 1).length;
  const totalPoints = guests.reduce((sum, g) => sum + (g.loyalty_points || 0), 0);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalGuests}</p>
            <p className="text-sm text-gray-500">Ospiti totali</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Repeat className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{returningGuests}</p>
            <p className="text-sm text-gray-500">Ricorrenti</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Euro className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">€{(totalSpent / 1000).toFixed(1)}k</p>
            <p className="text-sm text-gray-500">Revenue</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Award className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalPoints}</p>
            <p className="text-sm text-gray-500">Punti totali</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function GuestsListPage() {
  const { token } = useAuth();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  const fetchGuests = async (searchQuery = '') => {
    setLoading(true);
    try {
      const url = searchQuery 
        ? `${API}/admin/crm/guests?search=${encodeURIComponent(searchQuery)}&limit=100`
        : `${API}/admin/crm/guests?limit=100`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuests(data.guests || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
      toast.error('Errore nel caricamento ospiti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchGuests();
    }
  }, [token]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGuests(search);
  };

  const handleReset = () => {
    setSearch('');
    fetchGuests();
  };

  return (
    <AdminLayout title="Guest CRM">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Guest CRM</h1>
            <p className="text-gray-500 mt-1">Gestisci i tuoi ospiti e il programma fedeltà</p>
          </div>
          <button
            onClick={() => fetchGuests(search)}
            className="p-2.5 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors shadow-sm"
            title="Aggiorna"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <StatsSummary guests={guests} />

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, email, telefono, codice prenotazione..."
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent shadow-sm"
                data-testid="guest-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-xl font-medium transition-colors shadow-sm"
            >
              Cerca
            </button>
            {search && (
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 rounded-xl font-medium border border-gray-200 transition-colors shadow-sm"
              >
                Reset
              </button>
            )}
          </div>
        </form>

        {/* Results */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <span className="text-sm font-medium text-gray-700">
              {total} ospiti trovati
              {search && <span className="text-gray-500"> per &ldquo;{search}&rdquo;</span>}
            </span>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-10 h-10 mx-auto mb-3 text-[#C5A059] animate-spin" />
              <p className="text-gray-500 font-medium">Caricamento ospiti...</p>
            </div>
          ) : guests.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">Nessun ospite trovato</p>
              <p className="text-gray-400 text-sm mt-1">Prova a modificare i criteri di ricerca</p>
            </div>
          ) : (
            <div>
              {guests.map((guest, i) => (
                <GuestRow key={guest.id || i} guest={guest} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
